import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Sparkles, Download, RefreshCw, Store, Share2, Trash2, Copy, Wand2, Instagram,
  Smartphone, MessageCircle, ShoppingCart, Globe, PartyPopper, Flame, ShoppingBag, Loader2, ImageIcon,
} from "lucide-react";
import api, { errMsg, fmtINR, imgUrl, BACKEND } from "@/lib/api";
import { PageHeader, DemoBadge, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const TYPE_ICONS = {
  "shopping-bag": ShoppingBag, instagram: Instagram, smartphone: Smartphone, "message-circle": MessageCircle,
  "shopping-cart": ShoppingCart, globe: Globe, "party-popper": PartyPopper, flame: Flame,
};

const FMT_RATIO = { "1:1": "1/1", "4:5": "4/5", "9:16": "9/16", "16:9": "16/9" };

export default function AIStudio() {
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const [productId, setProductId] = useState(params.get("product") || "");
  const [ctype, setCtype] = useState("product_ad");
  const [template, setTemplate] = useState("");
  const [style, setStyle] = useState("Premium");
  const [format, setFormat] = useState("1:1");
  const [background, setBackground] = useState("Studio");
  const [language, setLanguage] = useState("English");
  const [offerText, setOfferText] = useState("");
  const [offerPrice, setOfferPrice] = useState("");
  const [brand, setBrand] = useState({ show_name: true, show_url: false, show_phone: false, cta: "Shop Now" });
  const [result, setResult] = useState(null);
  const [copyBusy, setCopyBusy] = useState(false);
  const [bannerTarget, setBannerTarget] = useState(null);

  const { data: config } = useQuery({ queryKey: ["ai-config"], queryFn: async () => (await api.get("/agent/ai/config")).data });
  const { data: products } = useQuery({ queryKey: ["agent-products-mini"], queryFn: async () => (await api.get("/agent/products?limit=100")).data });
  const { data: creatives, refetch: refetchCreatives } = useQuery({ queryKey: ["ai-creatives"], queryFn: async () => (await api.get("/agent/ai/creatives")).data });

  const product = (products?.items || []).find((p) => p.id === productId);

  const generate = useMutation({
    mutationFn: async () => (await api.post("/agent/ai/generate", {
      product_id: productId, creative_type: ctype, template, style, format, background, language,
      offer_text: offerText, offer_price: offerPrice ? Number(offerPrice) : null,
      original_price: product?.mrp || null, brand,
    })).data,
    onSuccess: async (d) => {
      setResult(d);
      toast.success(d.mode === "live" ? "Creative generated" : "Creative ready (demo mode)");
      qc.invalidateQueries({ queryKey: ["ai-config"] });
      refetchCreatives();
      setCopyBusy(true);
      try {
        const c = await api.post(`/agent/ai/${d.id}/copy`, { offer_price: offerPrice ? Number(offerPrice) : null });
        setResult((r) => ({ ...r, copy: c.data.copy, copy_mode: c.data.mode }));
      } catch { /* copy optional */ } finally { setCopyBusy(false); }
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const regenerate = useMutation({
    mutationFn: async (cid) => {
      const old = (creatives?.items || []).find((c) => c.id === cid);
      return (await api.post("/agent/ai/generate", {
        product_id: old.product_id, creative_type: old.creative_type, style: old.style, format: old.format,
        language: old.language, offer_text: old.offer_text, brand: { show_name: true, cta: "Shop Now" },
      })).data;
    },
    onSuccess: (d) => { toast.success("Regenerated"); setResult(d); refetchCreatives(); qc.invalidateQueries({ queryKey: ["ai-config"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const del = useMutation({
    mutationFn: async (cid) => api.delete(`/agent/ai/creatives/${cid}`),
    onSuccess: () => { toast.success("Deleted"); refetchCreatives(); if (result) setResult(null); },
  });

  const useOnStore = useMutation({
    mutationFn: async ({ cid, confirm }) => (await api.post(`/agent/ai/creatives/${cid}/use-on-store`, { confirm_overwrite: confirm })).data,
    onSuccess: (d, v) => {
      if (d.needs_confirm) return; // handled via dialog
      toast.success("Banner live on your store");
      setBannerTarget(null);
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const download = (url, name) => {
    const a = document.createElement("a");
    a.href = imgUrl(url);
    a.download = name || "creative.png";
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const shareWA = (c) => {
    const text = c.copy?.whatsapp_message || `${c.product_name} — check it out!`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  const copyText = (t, label) => { navigator.clipboard.writeText(t); toast.success(`${label} copied`); };

  const credits = config?.credits;

  return (
    <div data-testid="ai-studio-page">
      <PageHeader title="AI Marketing Studio" sub="Select a product → pick a creative → generate. No prompt writing needed." testid="ai-studio-title">
        {credits && (
          <span data-testid="ai-credits" className="rounded-full border border-slate-700 bg-[#111827] px-3 py-1.5 text-xs text-slate-300">
            <Sparkles size={12} className="mr-1 inline text-orange-400" />
            Credits today: <b className="text-slate-100">{credits.remaining_today} / {credits.daily_limit}</b>
          </span>
        )}
        {config?.mode === "demo" && <DemoBadge label="AI DEMO MODE" />}
      </PageHeader>

      <Tabs defaultValue="create">
        <TabsList className="border border-slate-800 bg-[#111827]">
          <TabsTrigger value="create" data-testid="tab-create">Create</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">My Creatives ({creatives?.items?.length || 0})</TabsTrigger>
        </TabsList>

        <TabsContent value="create" className="mt-4">
          <div className="grid gap-4 lg:grid-cols-[420px_1fr]">
            <div className="space-y-4 rounded-xl border border-slate-800 bg-[#111827] p-5" data-testid="generator-panel">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500">Product</Label>
                <Select value={productId} onValueChange={setProductId}>
                  <SelectTrigger data-testid="ai-product-select" className="mt-1.5 border-slate-700 bg-slate-900 text-slate-100">
                    <SelectValue placeholder="Select product" />
                  </SelectTrigger>
                  <SelectContent className="max-h-64 border-slate-700 bg-slate-900 text-slate-100">
                    {(products?.items || []).map((p) => (
                      <SelectItem key={p.id} value={p.id} data-testid={`ai-product-${p.sku}`}>{p.name} · {p.sku}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {product?.images?.[0] && (
                  <div className="mt-2 flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-900/60 p-2">
                    <img src={imgUrl(product.images[0])} alt="" className="h-10 w-10 rounded object-cover" />
                    <p className="text-[10px] text-slate-500">Master product image will be used as the AI reference automatically.</p>
                  </div>
                )}
              </div>

              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500">Creative type</Label>
                <div className="mt-1.5 grid grid-cols-3 gap-1.5">
                  {(config?.creative_types || []).map((t) => {
                    const Icon = TYPE_ICONS[t.icon] || Sparkles;
                    return (
                      <button key={t.id} data-testid={`ctype-${t.id}`} onClick={() => setCtype(t.id)}
                        className={`flex flex-col items-center gap-1 rounded-lg border p-2.5 text-center transition-colors ${ctype === t.id ? "border-orange-500 bg-orange-500/10 text-orange-400" : "border-slate-800 bg-slate-900/60 text-slate-400 hover:border-slate-700"}`}>
                        <Icon size={15} />
                        <span className="text-[9px] font-medium leading-tight">{t.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500">Campaign template (optional)</Label>
                <Select value={template} onValueChange={(v) => setTemplate(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="ai-template-select" className="mt-1.5 border-slate-700 bg-slate-900 text-slate-100">
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72 border-slate-700 bg-slate-900 text-slate-100">
                    <SelectItem value="none">None</SelectItem>
                    {Object.entries(config?.prompt_library || {}).map(([group, items]) => (
                      <div key={group}>
                        <p className="px-2 py-1 text-[9px] font-bold uppercase tracking-widest text-slate-500">{group}</p>
                        {items.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </div>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {[["Style", style, setStyle, config?.styles || [], "ai-style"], ["Background", background, setBackground, config?.backgrounds || [], "ai-background"]].map(([label, val, setter, opts, tid]) => (
                  <div key={label}>
                    <Label className="text-[10px] uppercase tracking-widest text-slate-500">{label}</Label>
                    <Select value={val} onValueChange={setter}>
                      <SelectTrigger data-testid={`${tid}-select`} className="mt-1.5 border-slate-700 bg-slate-900 text-slate-100"><SelectValue /></SelectTrigger>
                      <SelectContent className="border-slate-700 bg-slate-900 text-slate-100">
                        {opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>

              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500">Format</Label>
                <div className="mt-1.5 grid grid-cols-4 gap-1.5">
                  {(config?.formats || []).map((f) => (
                    <button key={f.id} data-testid={`format-${f.id.replace(":", "-")}`} onClick={() => setFormat(f.id)} title={f.hint}
                      className={`rounded-lg border py-2 text-[10px] font-bold transition-colors ${format === f.id ? "border-orange-500 bg-orange-500/10 text-orange-400" : "border-slate-800 bg-slate-900/60 text-slate-400"}`}>
                      {f.id}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-[10px] uppercase tracking-widest text-slate-500">Language</Label>
                <div className="mt-1.5 flex gap-1.5">
                  {(config?.languages || []).map((l) => (
                    <button key={l} data-testid={`lang-${l.toLowerCase()}`} onClick={() => setLanguage(l)}
                      className={`flex-1 rounded-lg border py-2 text-xs font-medium transition-colors ${language === l ? "border-orange-500 bg-orange-500/10 text-orange-400" : "border-slate-800 bg-slate-900/60 text-slate-400"}`}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-slate-500">Offer text</Label>
                  <Input data-testid="ai-offer-text" value={offerText} onChange={(e) => setOfferText(e.target.value)} placeholder="e.g. 20% OFF"
                    className="mt-1.5 border-slate-700 bg-slate-900 text-slate-100" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-slate-500">Offer price ₹</Label>
                  <Input data-testid="ai-offer-price" type="number" value={offerPrice} onChange={(e) => setOfferPrice(e.target.value)}
                    placeholder={product ? String(product.assignment?.price_store || product.suggested_price) : ""}
                    className="mt-1.5 border-slate-700 bg-slate-900 text-slate-100" />
                </div>
              </div>

              <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-3">
                <Label className="text-[10px] uppercase tracking-widest text-slate-500">Brand elements</Label>
                <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-300">
                  {[["show_name", "Store name"], ["show_url", "Website URL"], ["show_phone", "Phone"]].map(([k, label]) => (
                    <label key={k} className="flex cursor-pointer items-center gap-1.5">
                      <Checkbox data-testid={`brand-${k}`} checked={brand[k]} onCheckedChange={(c) => setBrand({ ...brand, [k]: !!c })} /> {label}
                    </label>
                  ))}
                </div>
                <Input data-testid="brand-cta" value={brand.cta} onChange={(e) => setBrand({ ...brand, cta: e.target.value })}
                  placeholder="CTA text (e.g. Shop Now)" className="mt-2 border-slate-700 bg-slate-900 text-xs text-slate-100" />
              </div>

              <Button data-testid="ai-generate-btn" onClick={() => generate.mutate()}
                disabled={!productId || generate.isPending || credits?.remaining_today === 0}
                className="w-full bg-orange-500 py-6 text-sm font-bold text-white shadow-[0_0_24px_rgba(255,87,34,0.3)] hover:bg-orange-600">
                {generate.isPending ? <Loader2 className="mr-2 animate-spin" size={15} /> : <Wand2 size={15} className="mr-2" />}
                {generate.isPending ? "Generating creative…" : "GENERATE"}
              </Button>
            </div>

            <div className="rounded-xl border border-slate-800 bg-[#111827] p-5" data-testid="result-panel">
              {!result && !generate.isPending && (
                <div className="flex h-full min-h-96 flex-col items-center justify-center text-center">
                  <ImageIcon size={36} className="text-slate-700" />
                  <p className="mt-3 text-sm text-slate-400">Your generated creative will appear here</p>
                  <p className="mt-1 max-w-xs text-[11px] text-slate-600">The product image, price and brand info are applied automatically — AI never invents specs or discounts.</p>
                </div>
              )}
              {generate.isPending && (
                <div className="flex h-full min-h-96 flex-col items-center justify-center" data-testid="generating-state">
                  <Loader2 className="animate-spin text-orange-500" size={32} />
                  <p className="mt-3 text-sm text-slate-400">Creating your {ctype.replace(/_/g, " ")}…</p>
                  <p className="mt-1 text-[11px] text-slate-600">Usually takes 10–30 seconds</p>
                </div>
              )}
              {result && !generate.isPending && (
                <div data-testid="generated-result">
                  <div className="mb-3 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-slate-200">Generated Creative</h3>
                    {result.mode === "demo" && <DemoBadge />}
                  </div>
                  <div className="mx-auto overflow-hidden rounded-xl border border-slate-700 bg-slate-950" style={{ maxWidth: format === "9:16" ? 260 : format === "16:9" ? "100%" : 380 }}>
                    <img data-testid="generated-image" src={imgUrl(result.image_url)} alt="AI creative" className="w-full object-contain" style={{ aspectRatio: FMT_RATIO[result.format] || "auto" }} />
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button data-testid="download-creative-btn" size="sm" onClick={() => download(result.image_url, `${result.sku}-${result.creative_type}.png`)}
                      className="bg-orange-500 text-white hover:bg-orange-600"><Download size={13} /> Download</Button>
                    <Button data-testid="regenerate-btn" size="sm" variant="outline" onClick={() => generate.mutate()} disabled={generate.isPending}
                      className="border-slate-700 text-slate-300 hover:bg-slate-800"><RefreshCw size={13} /> Regenerate</Button>
                    <Button data-testid="use-on-store-btn" size="sm" variant="outline" onClick={() => setBannerTarget(result.id)}
                      className="border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10"><Store size={13} /> Use on my store</Button>
                    <Button data-testid="share-whatsapp-btn" size="sm" variant="outline" onClick={() => shareWA(result)}
                      className="border-green-500/40 text-green-400 hover:bg-green-500/10"><Share2 size={13} /> Share</Button>
                  </div>
                  {copyBusy && (
                    <div className="mt-4 space-y-2" data-testid="copy-loading">
                      {[72, 56, 40].map((w) => (
                        <div key={w} className="h-9 animate-pulse rounded-lg border border-slate-800 bg-slate-900/60" style={{ width: `${w}%` }} />
                      ))}
                      <p className="flex items-center gap-2 text-xs text-slate-500"><Loader2 size={12} className="animate-spin" /> Writing copy in {result.language}…</p>
                    </div>
                  )}
                  {result.copy && (
                    <div className="mt-4 space-y-2" data-testid="generated-copy">
                      {[
                        ["Instagram caption", result.copy.caption],
                        ["WhatsApp message", result.copy.whatsapp_message],
                        ["Facebook caption", result.copy.facebook_caption],
                        ["Ad headline", result.copy.ad_headline],
                        ["CTA", result.copy.cta],
                        ["Hashtags", (result.copy.hashtags || []).join(" ")],
                      ].filter(([, v]) => v).map(([label, v]) => (
                        <div key={label} className="flex items-start justify-between gap-3 rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-2">
                          <div className="min-w-0">
                            <p className="text-[9px] font-bold uppercase tracking-widest text-slate-500">{label}</p>
                            <p className="mt-0.5 break-words text-xs text-slate-300">{v}</p>
                          </div>
                          <button data-testid={`copy-${label.replace(/ /g, "-").toLowerCase()}`} onClick={() => copyText(Array.isArray(v) ? v.join(" ") : v, label)}
                            className="shrink-0 rounded p-1.5 text-slate-500 hover:bg-slate-800 hover:text-orange-400"><Copy size={12} /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          {!creatives?.items?.length ? <EmptyState title="No creatives yet" sub="Generate your first AI creative." /> : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="creatives-grid">
              {creatives.items.map((c) => (
                <div key={c.id} data-testid={`creative-${c.id}`} className="group overflow-hidden rounded-xl border border-slate-800 bg-[#111827]">
                  <div className="relative">
                    <img src={imgUrl(c.image_url)} alt="" className="aspect-square w-full object-cover" />
                    {c.mode === "demo" && <span className="absolute left-1.5 top-1.5"><DemoBadge /></span>}
                  </div>
                  <div className="p-3">
                    <p className="truncate text-xs font-medium text-slate-200">{c.product_name}</p>
                    <p className="mt-0.5 text-[10px] capitalize text-slate-500">{c.creative_type.replace(/_/g, " ")} · {c.format} · {c.language}</p>
                    <div className="mt-2 flex gap-1">
                      <button data-testid={`creative-view-${c.id}`} onClick={() => setResult(c)} title="View/Reuse"
                        className="flex-1 rounded border border-slate-700 py-1 text-[10px] text-slate-300 hover:bg-slate-800">View</button>
                      <button data-testid={`creative-download-${c.id}`} onClick={() => download(c.image_url, `${c.sku}-${c.creative_type}.png`)} title="Download"
                        className="rounded border border-slate-700 px-2 py-1 text-slate-300 hover:bg-slate-800"><Download size={11} /></button>
                      <button data-testid={`creative-regen-${c.id}`} onClick={() => regenerate.mutate(c.id)} title="Regenerate"
                        className="rounded border border-slate-700 px-2 py-1 text-slate-300 hover:bg-slate-800"><RefreshCw size={11} /></button>
                      <button data-testid={`creative-store-${c.id}`} onClick={() => setBannerTarget(c.id)} title="Use on store"
                        className="rounded border border-emerald-500/40 px-2 py-1 text-emerald-400 hover:bg-emerald-500/10"><Store size={11} /></button>
                      <button data-testid={`creative-delete-${c.id}`} onClick={() => del.mutate(c.id)} title="Delete"
                        className="rounded border border-rose-500/40 px-2 py-1 text-rose-400 hover:bg-rose-500/10"><Trash2 size={11} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!bannerTarget} onOpenChange={() => setBannerTarget(null)}>
        <DialogContent className="border-slate-800 bg-[#111827] text-slate-100">
          <DialogHeader><DialogTitle>Use as store homepage banner?</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-400">This creative will be shown on your store's homepage, styled to match your current theme. If a banner already exists, it will be replaced.</p>
          <div className="flex gap-2">
            <Button data-testid="confirm-use-on-store" onClick={() => useOnStore.mutate({ cid: bannerTarget, confirm: true })}
              disabled={useOnStore.isPending} className="flex-1 bg-emerald-600 text-white hover:bg-emerald-700">Yes, use on my store</Button>
            <Button variant="outline" onClick={() => setBannerTarget(null)} className="border-slate-700 text-slate-300">Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
